import { ExternalLink } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { OnlineUserCounter } from './OnlineUserCounter'

interface StatusData {
  timestamp: string
  data: {
    websocket: {
      status: string
      last_message_time: string
      uptime_status: string
      total_messages_processed: number
    }
  }
}

const Footer = () => {
  const { t, i18n } = useTranslation()
  const [statusData, setStatusData] = useState<StatusData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  
  // Track initial load vs subsequent refreshes
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  
  // Flash animation state for status updates
  const [statusFlash, setStatusFlash] = useState(false)

  // Flash animation trigger function
  const triggerFlash = () => {
    setStatusFlash(true)
    setTimeout(() => setStatusFlash(false), 600) // Flash duration
  }

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        // Only show loading state during initial load
        if (isInitialLoad) {
          setIsLoading(true)
        }
        const response = await fetch('https://ping.nothing.co.th/api/status')
        const data = await response.json()
        if (data.success) {
          setStatusData(data)
          // Trigger flash animation for subsequent updates
          if (!isInitialLoad) {
            triggerFlash()
          }
        }
      } catch (error) {
        console.error('Failed to fetch status:', error)
      } finally {
        if (isInitialLoad) {
          setIsLoading(false)
          setIsInitialLoad(false)
        }
      }
    }

    fetchStatus()
    // Refresh status every 5 minutes
    const interval = setInterval(fetchStatus, 5 * 60 * 1000)
    
    return () => clearInterval(interval)
  }, [isInitialLoad])

  const formatLastUpdate = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    
    if (diffMinutes < 1) {
      return t('footer.justNow')
    } else if (diffMinutes < 60) {
      return `${diffMinutes} ${t('footer.minutesAgo')}`
    } else if (diffMinutes < 1440) {
      const hours = Math.floor(diffMinutes / 60)
      return `${hours} ${hours > 1 ? t('footer.hoursAgo') : t('footer.hourAgo')}`
    } else {
      return date.toLocaleString()
    }
  }

  // Get year in appropriate calendar system
  const getDisplayYear = () => {
    const currentYear = new Date().getFullYear()
    // Thai language uses Buddhist Era (BE) which is 543 years ahead of Common Era (CE)
    return i18n.language === 'th' ? currentYear + 543 : currentYear
  }

  return (
    <footer className="bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-8">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="text-center space-y-4">
          {/* System Status Section */}
          {!isLoading && statusData && (
            <div className={`text-xs text-gray-500 dark:text-gray-400 mb-2 transition-all duration-300 ${statusFlash ? 'scale-105 bg-green-100 dark:bg-green-900/50 rounded-lg px-3 py-2' : ''}`}>
              {/* Desktop Layout */}
              <div className="hidden sm:flex items-center justify-center space-x-4">
                <div className="flex items-center space-x-2">
                  <div className={`w-1.5 h-1.5 rounded-full ${
                    statusData.data.websocket.uptime_status === 'online' ? 'bg-green-500' : 'bg-red-500'
                  }`} />
                  <span>{t('footer.status')}: {statusData.data.websocket.status}</span>
                </div>
                
                <span className="text-gray-300">•</span>
                
                <span>{t('footer.lastMessage')}: {formatLastUpdate(statusData.data.websocket.last_message_time)}</span>
                
                <span className="text-gray-300">•</span>
                
                <span>{statusData.data.websocket.total_messages_processed.toLocaleString()} {t('footer.messages')}</span>
              </div>
              
              {/* Mobile Layout */}
              <div className="sm:hidden flex flex-col items-center space-y-1">
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${
                    statusData.data.websocket.uptime_status === 'online' ? 'bg-green-500' : 'bg-red-500'
                  }`} />
                  <span className="font-medium">{t('footer.systemOnline')}</span>
                </div>
                <span className="text-[10px] text-gray-400 dark:text-gray-500">
                  {t('footer.lastUpdate')}: {formatLastUpdate(statusData.data.websocket.last_message_time)}
                </span>
              </div>
            </div>
          )}

          <div className="text-sm text-gray-600 dark:text-gray-300">
            <p className="font-medium mb-3">{t('footer.dataSources')}</p>
            
            <div className="space-y-2 text-xs">
              <div className="flex flex-wrap items-center justify-center gap-4">
                <div className="flex items-center space-x-1">
                  <span className="text-gray-500 dark:text-gray-400">{t('footer.waterLevelApi')}</span>
                  <a
                    href="https://telerid.rid.go.th/#/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                  >
                    <span>@telerid.rid.go.th</span>
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
                
                <span className="text-gray-300">•</span>
                
                <div className="flex items-center space-x-1">
                  <span className="text-gray-500 dark:text-gray-400">{t('footer.cmuFloodSystem')}</span>
                  <a
                    href="https://watercenter.scmc.cmu.ac.th/cmflood/pole"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                  >
                    <span>@watercenter.scmc.cmu.ac.th</span>
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
              
              <p className="text-gray-500 dark:text-gray-400 mt-2">
                {t('footer.description')}
              </p>
            </div>
          </div>
          
          {/* Developer & Contact Info */}
          <div className="flex flex-col items-center space-y-2 text-xs text-gray-500 dark:text-gray-400">
            <div className="flex flex-wrap items-center justify-center gap-4">
              <div className="flex items-center space-x-1">
                <span>{t('footer.developedBy')}</span>
                <a
                  href="https://dejabrew.xyz/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                >
                  <span>DÉJÀ Brew Café and Gallery                  </span>
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              
              <span className="text-gray-300">•</span>
              
              <div className="flex items-center space-x-1">
                <span>{t('footer.contact')}</span>
                <a
                  href="mailto:ping@chiangmailivinghub.com"
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                >
                  ping@chiangmailivinghub.com
                </a>
              </div>
            </div>
            
            {/* Online User Counter */}
            <OnlineUserCounter 
              className="text-xs" 
              showConnectionStatus={true}
            />
          </div>
          
          {/* Copyright & Credits */}
          <div className="flex flex-col items-center space-y-2 text-xs text-gray-400 dark:text-gray-500 border-t border-gray-200 dark:border-gray-600 pt-4">
            <div>
              {getDisplayYear()} • {t('footer.copyright')}
            </div>
            
                         <div className="flex items-center justify-center space-x-1.5 text-[10px] text-gray-400 dark:text-gray-500">
               <a
                 href="https://www.instagram.com/sunoj"
                 target="_blank"
                 title="IN MING WE TRUST"
                 rel="noopener noreferrer"
                 className="hover:scale-110 transition-transform duration-200"
               >
                 <img src="/ming.png" alt="Ming" className="h-3.5 w-3.5 rounded-full border border-gray-300 dark:border-gray-600" />
               </a>
             </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer 